import {Component, computed, effect, inject, input, signal} from "@angular/core";
import {Translations} from "@translations";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {SimStatusPalette} from "../../models/SimStatusPalette";
import {EnumLabel} from "../../shared-pipes/enum-label.pipe";
import {DatePipe, NgClass} from "@angular/common";
import {explicitEffect} from "ngxtension/explicit-effect";

export class AutosaveConfig {
	timestamp?: string | Date | null;
	promise?: Promise<unknown> | null;
	warning?: string | null;
}

export enum AutosaveState {
	Success,
	Error,
	Busy,
	Warn,
}

@Component({
	selector: "sim-autosave-widget",
	templateUrl: "./autosave-widget.component.html",
	styleUrl: "./autosave-widget.component.scss",
	animations: [zoomInOnEnterAnimation({duration: 175}), zoomOutOnLeaveAnimation({duration: 175})],
	imports: [TranslateModule, NgClass, DatePipe],
})
export class AutosaveWidgetComponent {
	readonly translate = inject(TranslateService);
	// Inputs
	public timestamp = input.required<string | Date | undefined>();
	public variant = input<"vertical" | "horizontal">("horizontal");
	public promise = input<Promise<unknown> | null | undefined>(null);
	public warningMessage = input<string | null>(null);

	// Signals
	public saveTimestamp = signal<Date | null>(null);
	public state = signal<AutosaveState>(AutosaveState.Busy);
	public statusDisplay = computed<{color: SimStatusPalette; label: keyof typeof Translations}>(() =>
		this._computeStatusDisplay(this.state()),
	);

	// Properties
	public hint: string | null = null;

	// Readonly
	protected readonly T = Translations;
	protected readonly EnumLabel = EnumLabel;
	protected readonly AutosaveState = AutosaveState;

	// Injections
	private _translateService = inject(TranslateService);

	constructor() {
		// States are calculated to take the following priority: busy > error > warn > success
		explicitEffect([this.promise, this.warningMessage], () => this._effectPromiseChanges());
		effect(() => this._effectWarningChanges(this.warningMessage()));
		effect(() => this._effectSetTimestamp(this.timestamp()));
	}

	private _effectPromiseChanges(): void {
		const promise = this.promise();
		const warning = this.warningMessage();
		if (!promise) {
			this.state.set(AutosaveState.Success);
			return;
		}
		this.state.set(AutosaveState.Busy);
		const startTime = Date.now();
		promise
			.then(() => {
				this._resolveWithAnimationDelay(startTime, () => {
					if (warning) {
						this.state.set(AutosaveState.Warn);
						this.hint = this._translateService.instant(warning);
					} else {
						this.state.set(AutosaveState.Success);
						this.saveTimestamp.set(new Date());
					}
				});
			})
			.catch((error) => {
				this._resolveWithAnimationDelay(startTime, () => {
					this.state.set(AutosaveState.Error);
					this.hint = this._translateService.instant(this.T.ERROR_AUTOSAVE_WIDGET);
					console.error(error);
				});
			});
	}

	private _effectWarningChanges(warning: string | null): void {
		if (warning) {
			this.state.set(AutosaveState.Warn);
			this.hint = this._translateService.instant(warning);
		} else {
			this.state.set(AutosaveState.Success);
		}
	}

	private _effectSetTimestamp(timestamp: string | Date | undefined): void {
		if (timestamp) {
			this.saveTimestamp.set(new Date(timestamp));
		}
	}

	private _resolveWithAnimationDelay(startTime: number, resolveCallback: () => void) {
		const minimumDelay = 500;
		const elapsedTime = Date.now() - startTime;
		const delay = Math.max(0, minimumDelay - elapsedTime);
		setTimeout(resolveCallback, delay);
	}

	private _computeStatusDisplay(state: AutosaveState): {color: SimStatusPalette; label: keyof typeof Translations} {
		switch (state) {
			case AutosaveState.Success:
				return {color: SimStatusPalette.success, label: "AUTOSAVE_STATE_SUCCESS"};
			case AutosaveState.Error:
				return {color: SimStatusPalette.error, label: "AUTOSAVE_STATE_ERROR"};
			case AutosaveState.Busy:
				return {color: SimStatusPalette.busy, label: "AUTOSAVE_STATE_BUSY"};
			case AutosaveState.Warn:
				return {color: SimStatusPalette.warn, label: "AUTOSAVE_STATE_WARN"};
		}
	}
}
